import { useState, useEffect, useCallback } from "react";
import { T, S, fmtDate } from "./theme";
import { Badge, Btn, Modal, Drawer, ProviderDrawerContent, StationDrawerContent } from "./components";
import {
  fetchStationList, upsertStations, deleteStation as deleteStationApi,
  fetchProviders, uploadProviders, deleteProvider as deleteProviderApi,
  fetchSpecialties,
} from "./api";

export default function Admin({ showToast }) {
  const [adminSub, setAdminSub] = useState("stations");

  /* ── Data ── */
  const [stations, setStations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [providerPagination, setProviderPagination] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ── Station filters ── */
  const [stationSearch, setStationSearch] = useState("");

  /* ── Provider filters ── */
  const [provStationFilter, setProvStationFilter] = useState("");
  const [provSpecialtyFilter, setProvSpecialtyFilter] = useState("");
  const [provSearch, setProvSearch] = useState("");
  const [provPage, setProvPage] = useState(1);

  /* ── Modal + Drawer ── */
  const [modal, setModal] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const loadStations = useCallback(async () => {
    try {
      const res = await fetchStationList();
      setStations(res.data || []);
    } catch (err) {
      console.error("Failed to load stations:", err);
    }
  }, []);

  const loadProviders = useCallback(async (page = 1) => {
    try {
      const res = await fetchProviders({
        station_id: provStationFilter || undefined,
        specialty: provSpecialtyFilter || undefined,
        search: provSearch || undefined,
        page,
        limit: 50,
      });
      setProviders(res.data || []);
      setProviderPagination(res.pagination || null);
    } catch (err) {
      console.error("Failed to load providers:", err);
    }
  }, [provStationFilter, provSpecialtyFilter, provSearch]);

  useEffect(() => {
    const load = async () => {
      try {
        const [, spRes] = await Promise.all([loadStations(), fetchSpecialties()]);
        setSpecialties(spRes.specialties || []);
        await loadProviders();
      } catch (err) {
        console.error("Failed to load admin data:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) loadProviders(1);
  }, [provStationFilter, provSpecialtyFilter, provSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstUploadError = (res) => {
    const firstError = res?.errors?.[0];
    if (!firstError) return null;
    return firstError.error || "Upload failed";
  };

  /* ── Modal save handlers ── */
  const handleModalSave = async (e) => {
    const form = e.target.closest("div").parentElement;
    const inputs = form.querySelectorAll("input, select");
    const vals = {};
    inputs.forEach((el) => {
      const label = el.closest("div")?.querySelector("label")?.textContent;
      if (label) vals[label] = el.value;
    });

    try {
      if (modal?.type === "station") {
        await upsertStations([{
          station_id: vals["Station ID"],
          visn: parseInt(vals["VISN"]),
          site_name: vals["Site Name"],
          city: vals["City"],
        }]);
        showToast("Station added successfully");
        loadStations();
      } else {
        const res = await uploadProviders(2026, new Date().getMonth() + 1, [{
          secid: vals["SECID"],
          station_id: vals["Station ID"],
          provider_name: vals["Provider Name"],
          email: vals["Email"],
          specialty: vals["Specialty"],
          date_activated: vals["Date Activated"],
        }]);
        const uploadError = firstUploadError(res);
        if (uploadError) {
          throw new Error(uploadError);
        }
        showToast("Provider added successfully");
        loadProviders(1);
      }
    } catch (err) {
      showToast("Save failed: " + err.message);
    }
    setModal(null);
  };

  const handleDeleteStation = async (station_id) => {
    try {
      await deleteStationApi(station_id);
      showToast("Station deleted");
      loadStations();
    } catch (err) {
      showToast(err.message || "Failed to delete station");
    }
  };

  const handleDeleteProvider = async (secid) => {
    try {
      await deleteProviderApi(secid);
      showToast("Provider deleted");
      loadProviders(provPage);
    } catch (err) {
      showToast(err.message || "Failed to delete provider");
    }
  };

  /* ── Upload handler ── */
  const uploadYearOptions = ["2026", "2025"];
  const [uploadYear, setUploadYear] = useState(uploadYearOptions[0]);
  const [uploadMonth, setUploadMonth] = useState(() => String(new Date().getMonth() + 1));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.trim().split("\n");
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cols[i]; });
      return {
        secid: obj.secid || obj.sec_id,
        station_id: obj.station_id || obj["station id"],
        visn: obj.visn,
        site_name: obj.site_name || obj["site name"] || obj.site || obj.station_name || obj["station name"],
        city: obj.city,
        provider_name: obj.provider_name || obj["provider name"] || obj.name,
        email: obj.email,
        specialty: obj.specialty,
        date_activated: obj.date_activated || obj["date activated"],
      };
    });

    try {
      const res = await uploadProviders(parseInt(uploadYear), parseInt(uploadMonth), rows);
      showToast(`${res.inserted} inserted, ${res.updated} updated, ${res.errors?.length || 0} errors`);
      loadProviders(1);
    } catch (err) {
      showToast("Upload failed: " + err.message);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Loading admin...</div>;
  }

  return (
    <>
      <div style={S.subtabs}>
        {[{ key: "stations", label: "Stations" }, { key: "providers", label: "Providers" }, { key: "upload", label: "Upload Data" }].map(({ key, label }) => (
          <button key={key} style={S.subtab(adminSub === key)} onClick={() => setAdminSub(key)}>{label}</button>
        ))}
      </div>

      {/* ── Stations ── */}
      {adminSub === "stations" && (() => {
        const filteredStations = stations.filter((st) => {
          if (!stationSearch) return true;
          const q = stationSearch.toLowerCase();
          return (st.station_id || "").toLowerCase().includes(q) ||
                 (st.site_name || "").toLowerCase().includes(q) ||
                 (st.city || "").toLowerCase().includes(q);
        });
        return (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            <input style={S.filterInput} placeholder="Search stations..." value={stationSearch} onChange={(e) => setStationSearch(e.target.value)} />
          </div>
          <div style={S.tableWrap}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Stations</span>
              <Btn sm variant="primary" onClick={() => setModal({ type: "station" })}>+ Add Station</Btn>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Station ID", "VISN", "Site Name", "City", "Actions"].map((h) => <th key={h} style={{ ...S.th, ...(h === "Actions" ? { width: 120 } : {}) }}>{h}</th>)}</tr></thead>
              <tbody>
                {filteredStations.map((st) => (
                <tr key={st.station_id} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.surface2} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                  <td style={{ ...S.td, ...S.mono }}>{st.station_id}</td><td style={S.td}>{st.visn}</td><td style={S.td}>{st.site_name}</td><td style={S.td}>{st.city}</td>
                  <td style={S.td}>
                    <Btn sm variant="ghost" onClick={() => setDrawer({ type: "station-edit", station: st })}>Edit</Btn>{" "}
                    <Btn sm variant="red" onClick={() => handleDeleteStation(st.station_id)}>Del</Btn>
                  </td>
                </tr>
              ))}
              {filteredStations.length === 0 && (
                <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: T.textDim }}>No stations found</td></tr>
              )}
              </tbody>
            </table>
          </div>
        </>
        );
      })()}

      {/* ── Providers ── */}
      {adminSub === "providers" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <select style={S.filterSelect} value={provStationFilter} onChange={(e) => { setProvStationFilter(e.target.value); setProvPage(1); }}>
              <option value="">All Stations</option>
              {stations.map((st) => <option key={st.station_id} value={st.station_id}>{st.station_id} — {st.site_name}</option>)}
            </select>
            <select style={S.filterSelect} value={provSpecialtyFilter} onChange={(e) => { setProvSpecialtyFilter(e.target.value); setProvPage(1); }}>
              <option value="">All Specialties</option>
              {specialties.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
            </select>
            <input style={S.filterInput} placeholder="Search by name or SECID..." value={provSearch} onChange={(e) => { setProvSearch(e.target.value); setProvPage(1); }} />
          </div>
          <div style={S.tableWrap}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Providers</span>
              <Btn sm variant="primary" onClick={() => setModal({ type: "provider" })}>+ Add Provider</Btn>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["SECID", "Name", "Email", "Station", "Specialty", "Activated", "Actions"].map((h) => <th key={h} style={{ ...S.th, ...(h === "Actions" ? { width: 120 } : {}) }}>{h}</th>)}</tr></thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.secid} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.surface2} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                    <td style={{ ...S.td, ...S.mono }}>{p.secid}</td><td style={S.td}>{p.provider_name}</td><td style={{ ...S.td, ...S.muted }}>{p.email}</td><td style={S.td}>{p.station_id}</td>
                    <td style={S.td}><Badge>{p.specialty}</Badge></td><td style={S.td}>{fmtDate(p.date_activated)}</td>
                    <td style={S.td}>
                      <Btn sm variant="ghost" onClick={() => setDrawer({ type: "provider-edit", provider: p })}>Edit</Btn>{" "}
                      <Btn sm variant="red" onClick={() => handleDeleteProvider(p.secid)}>Del</Btn>
                    </td>
                  </tr>
                ))}
                {providers.length === 0 && (
                  <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: T.textDim }}>No providers found</td></tr>
                )}
              </tbody>
            </table>
            {providerPagination && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 12, color: T.textMuted }}>Showing {providers.length} of {providerPagination.total} providers</span>
                <div style={{ display: "flex", gap: 4 }}>
                  <Btn sm variant="ghost" onClick={() => { const p = Math.max(1, provPage - 1); setProvPage(p); loadProviders(p); }} style={{ opacity: provPage <= 1 ? 0.4 : 1 }}>Prev</Btn>
                  <Btn sm variant="ghost" onClick={() => { const p = provPage + 1; setProvPage(p); loadProviders(p); }} style={{ opacity: providers.length < 50 ? 0.4 : 1 }}>Next</Btn>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Upload ── */}
      {adminSub === "upload" && (
        <>
          <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Upload Provider Data</h2>
          <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 24 }}>Bulk upload provider activation records from the VA's monthly data file.</p>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 32, textAlign: "center", marginBottom: 20 }}>
            <label style={{ border: `2px dashed ${T.border}`, borderRadius: T.radius, padding: "48px 24px", cursor: "pointer", display: "block" }}>
              <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 8 }}>📁</div>
              <div style={{ fontSize: 14, color: T.text, marginBottom: 4 }}>Drop a CSV file here, or click to browse</div>
              <div style={{ fontSize: 12, color: T.textDim }}>Columns: VISN, Station ID, Provider Name, Email, Specialty, SECID, Date Activated</div>
              <input type="file" accept=".csv" onChange={handleFileUpload} style={{ display: "none" }} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <select style={S.filterSelect} value={uploadYear} onChange={(e) => setUploadYear(e.target.value)}>
              {uploadYearOptions.map((year) => (
                <option key={year} value={year}>Report Year: {year}</option>
              ))}
            </select>
            <select style={S.filterSelect} value={uploadMonth} onChange={(e) => setUploadMonth(e.target.value)}>
              {["January","February","March","April","May","June","July","August","September","October","November","December"].map((name, i) => (
                <option key={i + 1} value={i + 1}>Report Month: {name}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* ── Add Modal ── */}
      <Modal
        open={!!modal}
        title={modal?.type === "station" ? "Add Station" : "Add Provider"}
        fields={modal?.type === "station"
          ? [{ label: "Station ID", placeholder: "e.g. 674" }, { label: "VISN", type: "number", placeholder: "e.g. 17" }, { label: "Site Name", placeholder: "e.g. Central Texas VA" }, { label: "City", placeholder: "e.g. Temple" }]
          : [{ label: "SECID", placeholder: "e.g. SEC12345" }, { label: "Station ID", placeholder: "e.g. 674" }, { label: "Provider Name", placeholder: "e.g. Dr. Smith" }, { label: "Email", type: "email", placeholder: "e.g. smith@va.gov" }, { label: "Specialty", placeholder: "e.g. Primary Care" }, { label: "Date Activated", type: "date" }]
        }
        onClose={() => setModal(null)}
        onSave={handleModalSave}
      />

      {/* ── Edit Drawers ── */}
      <Drawer
        open={!!drawer}
        title={drawer?.type === "provider-edit" ? "Edit Provider" : drawer?.type === "station-edit" ? "Edit Station" : ""}
        onClose={() => setDrawer(null)}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setDrawer(null)}>Cancel</Btn>
            <Btn variant="red" onClick={() => {
              if (drawer?.type === "provider-edit") handleDeleteProvider(drawer.provider.secid);
              else if (drawer?.type === "station-edit") handleDeleteStation(drawer.station.station_id);
              setDrawer(null);
            }}>Delete</Btn>
            <Btn variant="primary" onClick={() => { setDrawer(null); showToast("Saved successfully"); }}>Save Changes</Btn>
          </>
        }
      >
        {drawer?.type === "provider-edit" && <ProviderDrawerContent provider={drawer.provider} specialties={specialties} />}
        {drawer?.type === "station-edit" && <StationDrawerContent station={drawer.station} editable />}
      </Drawer>
    </>
  );
}
